import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import FamilyMeetingMinutesForm from "./FamilyMeetingMinutesForm";
import { createFamilyMeetingMinute } from "../../../store/slices/familyMeetingMinutesSlice";
import { getApiErrorMessage } from "../../../utils/apiError";
import { fetchPatient } from "../../../store/slices/patientsSlice"; // Added this import

const AddFamilyMeetingMinutes = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isLoading, error } = useSelector((state) => state.familyMeetingMinutes);
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [errors, setErrors] = useState({});
    const [prefilledName, setPrefilledName] = useState(""); // ⭐ ADD THIS LOCAL STATE VARIABLE

    // ⭐ ADD THIS PATIENT FETCHING HOOK BLOCK ⭐
    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        const fullName = `${patient.first_name || ""} ${patient.last_name || ""}`.trim();
                        setPrefilledName(fullName);
                    }
                })
                .catch((err) => console.error("Failed to fetch patient for meeting minutes:", err));
        }
    }, [dispatch, patientId]);

    const handleSubmit = async (formData) => {
        // Convert camelCase → snake_case (parent + notes repeater)
        const payload = {
            patient_id: patientId || null,
            case_id: caseId || null,
            client_name: formData.clientName,
            notes: (formData.notes || []).map((n) => ({
                report_written_by: n.reportWrittenBy,
                meeting_date: n.meetingDate,
                agenda: n.agenda,
                summary: n.summary,
                action_plan: n.actionPlan,
                family_members: (n.familyMembers || []).filter((m) => m.name?.trim()).map((m) => ({ name: m.name })),
                team_members: (n.teamMembers || []).filter((m) => m.name?.trim()).map((m) => ({ name: m.name })),
            })),
        };

        // Frontend validation
        const newErrors = {};
        if (!payload.client_name) newErrors.client_name = "Client name is required.";
        if (!payload.notes.length) newErrors.notes = "Add at least one month.";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            const res = await dispatch(createFamilyMeetingMinute(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Family meeting minutes added successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/phase05/family-meeting-minutes/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            console.error("Create error:", err);
            setErrors(err?.errors || {});
            Swal.fire({
                title: "Error",
                text: getApiErrorMessage(err, "Failed to save record."),
                icon: "error",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                <div className="card">
                    <div className="card-body">
                        {/* API Error */}
                        {error && (
                            <div className="alert alert-danger">
                                <strong>Error:</strong> {error}
                            </div>
                        )}

                        {/* Validation Errors */}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.entries(errors).map(([key, value], i) => (
                                    <div key={i}>
                                        {Array.isArray(value) ? value.join(", ") : value}
                                    </div>
                                ))}
                            </div>
                        )}

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                const form = e.target;
                                const formData = form.__formData__;
                                if (formData) handleSubmit(formData);
                            }}
                        >
                            <FamilyMeetingMinutesForm
                                initialData={{
                                    clientName: prefilledName,
                                    patientId,
                                    caseId
                                }} // ⭐ PASS PREFILLED DETAILS HERE
                                onChange={(data) => {
                                    const form = document.querySelector("form");
                                    if (form) form.__formData__ = data;
                                }}
                            />

                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4 sticky-form-submit"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-3-line me-1"></i> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>

                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddFamilyMeetingMinutes;